import { useEffect, useRef } from "react";
import { useAppNavigate } from "./useAppNavigate";
import { useWebSocket } from "../context/WebSocketContext";
import { useAuth } from "../context/AuthContext";
import { useAppContext } from "../context/AppContext";
import { fetchPatientById } from "../apis/patient";
import { mapPatientToUserData } from "../utils/helpers";
import { logSecretly } from "../utils/logSecretly";
import { WS_EVENTS } from "../constants";
import type { SessionStartedEvent } from "../dtos/websocket";

/**
 * Listens for the WebSocket SESSION_STARTED event, fetches the patient,
 * and logs in. Should be mounted once (e.g. in the Welcome page or App root).
 */
export function useSessionStartedListener(): void {
    const navigate = useAppNavigate();
    const { subscribe } = useWebSocket();
    const { persistSession } = useAuth();
    const { setUserData } = useAppContext();
    const isFetchingRef = useRef(false);

    useEffect(() => {
        const unsubscribe = subscribe(WS_EVENTS.SESSION_STARTED, async (data: SessionStartedEvent) => {
            if (isFetchingRef.current) return;
            isFetchingRef.current = true;

            try {
                const patient = await fetchPatientById(data.patientId);
                const userData = mapPatientToUserData(patient, data.sessionId, data.podIp);
                persistSession(userData);
                setUserData(userData);
                navigate("/dashboard");
            } catch (err) {
                logSecretly("Failed to load patient after session start:", err);
                isFetchingRef.current = false;
            }
        });
        return () => unsubscribe();
    }, [subscribe, setUserData, navigate, persistSession]);
}
